import { SessionListener } from "../../core/internal/session/SessionListener"
import { SessionManager } from "../../core/internal/session/SessionManager"
import { SessionContext } from "../../core/internal/session/SessionContext"
import { SessionPolicy } from "../../core/internal/session/SessionPolicy"
import { UserListener } from "../../core/internal/user/UserListener"
import { User } from "../../index.browser"
import { PropertyOperations } from "../property/PropertyOperations"
import { IStorage } from "../../core/internal/storage/Storage"
import { createSessionId } from "../../core/internal/session/Session"
import { LAST_EVENT_TIME_STORAGE_KEY, SESSION_ID_STORAGE_KEY } from "../../config"
import { Lifecycle } from "../../core/internal/lifecycle/Lifecycle"
import { LifecycleChangeListener } from "../../core/internal/lifecycle/LifecycleChangeListener"
import { isSameUser } from "../../core/internal/user/UserManager"
import Logger from "../../core/internal/logger"

export class SessionManagerImpl implements SessionManager, UserListener, LifecycleChangeListener {
  public static EMPTY_SESSION_ID = "0.ffffffff"

  private sessionListeners: SessionListener[] = []

  private _currentSessionId: string | null = null
  private _lastEventTime: number | null = null

  constructor(private sessionPolicy: SessionPolicy, private storage: IStorage) {
    this.loadSession()
    this.loadLastEventTime()
  }

  public get currentSessionId(): string | null {
    return this._currentSessionId
  }

  public get sessionId(): string {
    return this._currentSessionId ?? SessionManagerImpl.EMPTY_SESSION_ID
  }

  public get lastEventTime(): number | null {
    return this._lastEventTime
  }

  public addListener(listener: SessionListener): void {
    this.sessionListeners.push(listener)
  }

  public startNewSession(oldUser: User | null, newUser: User, timestamp: number): string {
    if (oldUser) {
      this.endSession(oldUser)
    }
    return this.newSession(newUser, timestamp)
  }

  public startNewSessionIfNeeded(context: SessionContext): string {
    if (this.shouldStartNewSession(context)) {
      return this.startNewSession(context.oldUser, context.newUser, context.timestamp)
    } else {
      this.updateLastEventTime(context.timestamp)
      return this.sessionId
    }
  }

  public updateLastEventTime(timestamp: number): void {
    this._lastEventTime = timestamp
    this.storage.setItem(LAST_EVENT_TIME_STORAGE_KEY, `${timestamp}`)
  }

  private shouldStartNewSession(context: SessionContext): boolean {
    if (this._currentSessionId === null) return true

    if (!isSameUser(context.oldUser, context.newUser)) {
      try {
        if (!this.sessionPolicy.persistCondition(context.oldUser, context.newUser)) return true
      } catch (e) {
        Logger.log.warn(`persistCondition threw an error, starting new session: ${e}`)
        return true
      }
    }

    return this.isSessionTimedOut(context.timestamp)
  }

  private isSessionTimedOut(timestamp: number): boolean {
    if (this._lastEventTime === null) return true
    return timestamp - this._lastEventTime >= this.sessionPolicy.timeoutMillis
  }

  private endSession(user: User) {
    const currentSessionId = this._currentSessionId
    const lastEventTime = this._lastEventTime
    if (currentSessionId !== null && lastEventTime !== null) {
      this.sessionListeners.forEach((listener) => listener.onSessionEnded(currentSessionId, user, lastEventTime))
    }
  }

  private newSession(user: User, timestamp: number): string {
    const newSessionId = createSessionId(timestamp)
    this.saveSession(newSessionId)
    this.updateLastEventTime(timestamp)
    this.sessionListeners.forEach((listener) => {
      listener.onSessionStarted(newSessionId, user, timestamp)
    })
    return newSessionId
  }

  private saveSession(sessionId: string) {
    this._currentSessionId = sessionId
    this.storage.setItem(SESSION_ID_STORAGE_KEY, sessionId)
  }

  private loadSession() {
    this._currentSessionId = this.storage.getItem(SESSION_ID_STORAGE_KEY)
  }

  private loadLastEventTime() {
    const lastEventTimeStr = this.storage.getItem(LAST_EVENT_TIME_STORAGE_KEY)
    if (lastEventTimeStr !== null) {
      const lastEventTime = Number.parseFloat(lastEventTimeStr)
      if (lastEventTime > 0) {
        this._lastEventTime = lastEventTime
      }
    }
  }

  onUserUpdated(oldUser: User, newUser: User, timestamp: number): void {
    this.startNewSessionIfNeeded({ oldUser, newUser, timestamp })
  }

  onPropertyOperations(_user: User, _operations: PropertyOperations, _timestamp: number): void {
    // noop
  }

  onLifecycleChanged(lifecycle: Lifecycle, timestamp: number): void {
    switch (lifecycle) {
      case "hidden":
      case "pagehide":
        return this.updateLastEventTime(timestamp)
      case "blur":
      case "focus":
      case "locationChange":
      case "visible":
      case "pageshow":
        return
    }
  }
}
